<script setup lang="ts">
import WrButton from "~/components/global/WrButton.vue";

withDefaults(
  defineProps<{
    ctaLink?: string | null;
  }>(),
  {
    ctaLink: null,
  },
);

const emit = defineEmits<{
  "cta-clicked": [];
}>();

const heroVisible = ref(false);
const frameTime = ref(8.3);
const sparklinePath = ref('M0,45 C15,42 30,38 50,30 C70,22 90,15 110,18 C130,21 150,8 170,12 C190,16 210,4 230,6 C250,8 280,3 300,2');
const sparklineArea = ref('');

let frameTimer: ReturnType<typeof setInterval> | null = null;
let sparkTimer: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  setTimeout(() => heroVisible.value = true, 200);
  const frameTimes = [8.1, 8.3, 8.2, 8.4, 8.3, 8.0, 8.5, 8.3, 8.2, 8.3];
  let fi = 0;
  frameTimer = setInterval(() => { frameTime.value = frameTimes[(fi++) % frameTimes.length] ?? 8.3; }, 2000);
  const sparklines = [
    'M0,45 C15,42 30,38 50,30 C70,22 90,15 110,18 C130,21 150,8 170,12 C190,16 210,4 230,6 C250,8 280,3 300,2',
    'M0,44 C15,40 30,36 50,28 C70,20 90,14 110,16 C130,19 150,10 170,10 C190,14 210,6 230,5 C250,7 280,4 300,3',
    'M0,46 C15,43 30,39 50,32 C70,24 90,16 110,20 C130,23 150,10 170,14 C190,18 210,5 230,7 C250,9 280,2 300,1',
  ];
  let si = 0;
  sparkTimer = setInterval(() => {
    si = (si + 1) % sparklines.length;
    sparklinePath.value = sparklines[si]!;
    sparklineArea.value = sparklines[si]! + ' L300,50 L0,50 Z';
  }, 3000);
});

onUnmounted(() => {
  if (frameTimer) clearInterval(frameTimer);
  if (sparkTimer) clearInterval(sparkTimer);
});

const progressBars = [
  { label: 'iOS & iPadOS', value: 98, gradient: '#2563EB,#7C3AED' },
  { label: 'Android & wearOS', value: 98, gradient: '#7C3AED,#BA55FE' },
  { label: 'Web', value: 90, gradient: '#2563EB,#BA55FE' },
  { label: 'Desktop', value: 90, gradient: '#BA55FE,#12DBFF' },
];
</script>

<template>
  <section class="relative overflow-hidden pt-12 pb-12 sm:pb-16 lg:pb-20 flutter-hero">
    <div class="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 relative z-10">

      <!-- Heading -->
      <div class="relative z-10 text-center max-w-7xl mx-auto">
        <h1 class="flutter-heading">Transform Your App Vision</h1>
        <h2 class="flutter-subheading">Fast, Seamless, Flutter</h2>
        <p class="flutter-tagline">Fast, interactive apps that captivate.</p>
      </div>

      <!-- CTA -->
      <div class="text-center mt-6 mb-8 lg:mb-14">
        <WrButton
            :link="ctaLink || undefined"
            @click="!ctaLink ? emit('cta-clicked') : undefined"
            container-class="inline-flex items-center h-[48px] lg:h-[56px] min-w-[min(100%,320px)] max-w-full bg-none p-0"
            append-class="px-8 bg-[linear-gradient(95.99deg,#586BCC_3.69%,#23216C_96.65%)] text-[clamp(14px,1.6vw,20px)]"
          >
            Start Your Flutter Project Today
        </WrButton>
      </div>

      <!-- Hero Visual Layout -->
      <div class="relative flex flex-col items-center gap-8 mt-2 lg:flex-row lg:justify-center lg:gap-12 lg:items-center">

        <!-- 3D Orbit & Center Logo Area (Positioned first on mobile for central focus) -->
        <div class="relative flex items-center justify-center flex-shrink-0 flutter-logo-area order-1 lg:order-2 my-2 lg:my-0">
          <!-- Background Glow Elements -->
          <div class="flutter-gw1" /><div class="flutter-gw2" /><div class="flutter-gp1" /><div class="flutter-gp2" /><div class="flutter-gb" />
          
          <!-- Central Flutter Logo -->
          <div class="center-logo-wrapper transition-all duration-1000 ease-out" :style="{ transform: heroVisible ? 'scale(1) translateZ(30px)' : 'scale(0.85) translateZ(30px)', opacity: heroVisible ? 1 : 0 }">
            <NuxtImg 
              src="/images/services/flutter-logo.webp" 
              alt="Flutter Logo" 
              class="flutter-logo-img"
              preload
              fetchpriority="high"
            />
          </div>

          <!-- Tilted 3D Orbit Track -->
          <div class="orbit-3d-stage">
            <div class="orbit-3d-track">
              <!-- Oval ring visual -->
              <div class="flutter-ring-path" />

              <!-- iOS Badge Node -->
              <div class="orbit-node flutter-pbadge-ios">
                <div class="icon-counter-tilt">
                  <NuxtImg 
                    src="/images/services/svg/ios.svg" 
                    alt="iOS" 
                    class="flutter-picon"
                    preload
                    fetchpriority="high"
                  />
                </div>
              </div>

              <!-- Android Badge Node -->
              <div class="orbit-node flutter-pbadge-android">
                <div class="icon-counter-tilt">
                  <NuxtImg 
                    src="/images/services/svg/android.svg" 
                    alt="Android" 
                    class="flutter-picon"
                    preload
                    fetchpriority="high"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Impeller GPU Panel -->
        <div class="w-full sm:w-auto order-2 lg:order-1 flex justify-center">
          <div class="flutter-panel flutter-panel-impeller">
            <div class="flutter-panel-header">
              <div class="flex items-center gap-2.5">
                <span class="flutter-icon">
                  <svg class="w-[16px] h-[16px]" viewBox="0 0 21 21" fill="none">
                    <rect x="5" y="5" width="11" height="11" rx="2" stroke="#E9E7FE" stroke-width="1.5"/>
                    <rect x="8" y="8" width="5" height="5" rx="1" fill="#E9E7FE"/>
                    <line x1="3" y1="7" x2="5" y2="7" stroke="#E9E7FE" stroke-width="1"/>
                    <line x1="3" y1="10" x2="5" y2="10" stroke="#E9E7FE" stroke-width="1"/>
                    <line x1="3" y1="13" x2="5" y2="13" stroke="#E9E7FE" stroke-width="1"/>
                    <line x1="16" y1="7" x2="18" y2="7" stroke="#E9E7FE" stroke-width="1"/>
                    <line x1="16" y1="10" x2="18" y2="10" stroke="#E9E7FE" stroke-width="1"/>
                    <line x1="16" y1="13" x2="18" y2="13" stroke="#E9E7FE" stroke-width="1"/>
                  </svg>
                </span>
                <div>
                  <div class="flutter-panel-title">IMPELLER GPU</div>
                  <div class="flutter-panel-sub">METAL + VULKAN</div>
                </div>
              </div>
              <span class="flutter-pill flutter-pill-purple">120 FPS</span>
            </div>
            <div class="flex items-baseline justify-between mb-3">
              <div class="flex items-baseline gap-1.5">
                <span class="flutter-frame">{{ frameTime }}</span>
                <span class="flutter-unit">ms</span>
                <span class="flutter-label">avg frame</span>
              </div>
              <span class="flutter-pill flutter-pill-green">0 Jank</span>
            </div>
            <div class="flutter-chart">
              <svg class="flutter-chart-svg" viewBox="0 0 300 50" preserveAspectRatio="none">
                <path :d="sparklinePath" fill="none" stroke="#ABA5FF" stroke-width="1.5" class="flutter-line"/>
                <path :d="sparklineArea" fill="url(#sparkGrad)" opacity="0.3"/>
                <defs><linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="rgba(168,85,247,0.7)"/><stop offset="100%" stop-color="rgba(168,85,247,0)"/>
                </linearGradient></defs>
              </svg>
              <div class="flutter-chart-labels">
                <span class="flutter-label">0ms</span><span class="text-[#C084FC] flutter-label">120 FPS</span><span class="flutter-label">Live</span>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2 mt-4">
              <div class="flutter-stat">
                <div class="flutter-stat-label">SHADER JANK</div>
                <div class="flutter-stat-val text-[#34D399]">0.0%</div>
                <div class="flutter-stat-sub">Precompiled</div>
              </div>
              <div class="flutter-stat">
                <div class="flutter-stat-label">BINARY</div>
                <div class="flutter-stat-val text-[#C084FC]">Native ARM</div>
                <div class="flutter-stat-sub">No WebView</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Code Unification Panel -->
        <div class="w-full sm:w-auto order-3 flex justify-center">
          <div class="flutter-panel flutter-panel-code">
            <div class="flutter-panel-header">
              <div class="flex items-center gap-2.5">
                <span class="flutter-icon">
                  <svg class="w-[16px] h-[16px]" viewBox="0 0 21 21" fill="none">
                    <path d="M6 4h9a2 2 0 012 2v9a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2z" stroke="#E9E7FE" stroke-width="1.5"/>
                    <path d="M8 8h5M8 11h5M8 14h3" stroke="#E9E7FE" stroke-width="1.2" stroke-linecap="round"/>
                  </svg>
                </span>
                <div>
                  <div class="flutter-panel-title">ONE CODEBASE</div>
                  <div class="flutter-panel-sub">MOBILE · WEB · DESKTOP</div>
                </div>
              </div>
              <span class="flutter-pill flutter-pill-green">Dart 3</span>
            </div>
            <div class="mb-4">
              <span class="flutter-unify">90<span class="text-lg">%</span></span>
              <div class="flutter-label mt-1">code reused across platforms</div>
            </div>
            <div class="space-y-2.5 mb-4">
              <div v-for="bar in progressBars" :key="bar.label">
                <div class="flex justify-between items-center mb-1">
                  <div class="flex items-center gap-1.5">
                    <span class="flutter-dot"/>
                    <span class="flutter-label">{{ bar.label }}</span>
                  </div>
                  <span class="flutter-percent text-[#34D399]">{{ bar.value }}%</span>
                </div>
                <div class="flutter-track">
                  <div class="flutter-fill" :style="{ width: bar.value + '%', background: `linear-gradient(90deg,${bar.gradient})` }"/>
                </div>
              </div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div class="flutter-stat">
                <div class="flutter-stat-label">COST VS NATIVE</div>
                <div class="flutter-stat-val text-[#34D399]">~40%</div>
                <div class="flutter-stat-sub">One codebase</div>
              </div>
              <div class="flutter-stat">
                <div class="flutter-stat-label">HOT RELOAD</div>
                <div class="flutter-stat-val text-[#34D399]">&lt;1s</div>
                <div class="flutter-stat-sub">Sub-second</div>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </section>
</template>

<style scoped>
/* ─── Headings ─── */
.flutter-heading {
  font-size: clamp(32px, 5vw, 78px); line-height: 1.15;
  font-weight: 600; letter-spacing: -0.02em; text-align: center;
  background: linear-gradient(92.15deg, #12DBFF -3.57%, #895AF5 80.72%);
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
}
.flutter-subheading {
  font-size: clamp(28px, 4.5vw, 78px); line-height: 1.2;
  font-weight: 600; letter-spacing: -0.02em; text-align: center; color: #fff; margin-top: 4px;
}
.flutter-tagline {
  font-size: clamp(15px, 2vw, 24px); line-height: 1.4;
  color: #fff; letter-spacing: 0.05em; text-align: center; margin-top: 12px;
}
/* Light theme */
html:not(.my-app-dark) .flutter-subheading { color: #23216c; }
html:not(.my-app-dark) .flutter-tagline { color: #444; }

/* ─── Logo Area & 3D Stage ─── */
.flutter-logo-area {
  position: relative;
  width: clamp(240px, 72vw, 420px);
  height: clamp(240px, 72vw, 440px);
  perspective: 1200px;
  transform-style: preserve-3d;
}

.center-logo-wrapper {
  position: relative;
  z-index: 10;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.flutter-logo-img { 
  width: 80%; 
  height: 80%; 
  object-fit: contain; 
}

/* ─── Logo Glows ─── */
.flutter-gw1 { position: absolute; width: 100px; height: 6px; right: 20%; top: 30%; background: #fff; filter: blur(18px); }
.flutter-gw2 { position: absolute; width: 24px; height: 24px; right: 25%; top: 15%; background: #fff; filter: blur(26px); }
.flutter-gp1 { position: absolute; width: 200px; height: 200px; left: -50px; top: -30px; background: #645fff; opacity: 0.5; filter: blur(100px); border-radius: 50%; }
.flutter-gp2 { position: absolute; width: 200px; height: 200px; right: -40px; top: -20px; background: #645fff; opacity: 0.5; filter: blur(100px); border-radius: 50%; }
.flutter-gb { position: absolute; width: 70px; height: 100px; bottom: 10%; left: 45%; background: #0194fd; filter: blur(70px); border-radius: 50%; transform: rotate(-41deg); }

/* ─── 3D Orbit Mechanics ─── */
.orbit-3d-stage {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  transform-style: preserve-3d;
}

.orbit-3d-track {
  position: absolute;
  width: 400px;
  height: 400px;
  transform-style: preserve-3d;
  transform: rotateX(68deg) rotateY(-15deg);
}

@media (max-width: 640px) {
  .orbit-3d-track {
    width: 260px;
    height: 260px;
  }
}

.flutter-ring-path {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1.5px solid rgba(59, 130, 255, 0.4);
  box-shadow:
    0 0 20px rgba(59, 130, 255, 0.25),
    inset 0 0 20px rgba(59, 130, 255, 0.15);
}

.orbit-node {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 56px;
  height: 56px;
  margin-top: -28px;
  margin-left: -28px;
  z-index: 5;
  transform-style: preserve-3d;
  /* Static fallback (reduced-motion / pre-animation): sits on the right side of the ring.
     The orbit-spin keyframes override this while the animation runs. */
  transform: translateX(130px) rotateY(15deg) rotateX(-68deg);
  animation: orbit-spin 8s linear infinite;
}

@media (min-width: 640px) {
  .orbit-node {
    width: 60px;
    height: 60px;
    margin-top: -30px;
    margin-left: -30px;
    transform: translateX(200px) rotateY(15deg) rotateX(-68deg);
  }
}

.flutter-pbadge-ios {
  border: 1px solid rgba(59,130,246,0.35);
  box-shadow: 0 0 18px rgba(59,130,246,0.25);
  background: rgba(12, 20, 50, 0.85);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
}

.flutter-pbadge-android {
  border: 1px solid rgba(52,211,153,0.35);
  box-shadow: 0 0 18px rgba(52,211,153,0.25);
  background: rgba(12, 20, 50, 0.85);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(8px);
  /* Static fallback: opposite side of the ring (left) */
  transform: rotateZ(180deg) translateX(130px) rotateZ(-180deg) rotateY(15deg) rotateX(-68deg);
  animation-delay: -4s;
}

@media (min-width: 640px) {
  .flutter-pbadge-android {
    transform: rotateZ(180deg) translateX(200px) rotateZ(-180deg) rotateY(15deg) rotateX(-68deg);
  }
}

.icon-counter-tilt {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.flutter-picon { width: 24px; height: 24px; object-fit: contain; }

@media (min-width: 640px) {
  .flutter-picon { width: 26px; height: 26px; }
}

@keyframes orbit-spin {
  0% {
    transform: rotateZ(0deg) translateX(130px) rotateZ(0deg) rotateY(15deg) rotateX(-68deg);
  }
  100% {
    transform: rotateZ(360deg) translateX(130px) rotateZ(-360deg) rotateY(15deg) rotateX(-68deg);
  }
}

/* The orbit is a signature element of this hero — keep it spinning even for
   users with the OS reduced-motion setting (global rule in critical.css). */
@media (prefers-reduced-motion: reduce) {
  .orbit-node {
    animation-duration: 8s !important;
    animation-iteration-count: infinite !important;
  }
}

@media (min-width: 640px) {
  @keyframes orbit-spin {
    0% {
      transform: rotateZ(0deg) translateX(200px) rotateZ(0deg) rotateY(15deg) rotateX(-68deg);
    }
    100% {
      transform: rotateZ(360deg) translateX(200px) rotateZ(-360deg) rotateY(15deg) rotateX(-68deg);
    }
  }
}

/* ─── Panels ─── */
.flutter-panel {
  width: 100%;
  max-width: 340px;
  background: linear-gradient(118.11deg, #0B0E3E 4.47%, #101E63 79.8%);
  border-radius: 12px; border: 1px solid rgba(136,127,255,0.15);
  padding: 16px; overflow: hidden;
  transition: border-color 0.3s, box-shadow 0.3s, transform 0.3s;
}

@media (min-width: 1024px) {
  .flutter-panel {
    width: clamp(220px, 24vw, 270px);
    max-width: none;
  }
}

.flutter-panel-impeller:hover {
  border-color: rgba(168,85,247,0.4);
  box-shadow: 0 0 30px rgba(168,85,247,0.15), 0 0 60px rgba(168,85,247,0.05);
  transform: translateY(-2px);
}
.flutter-panel-code:hover {
  border-color: rgba(52,211,153,0.35);
  box-shadow: 0 0 30px rgba(52,211,153,0.12), 0 0 60px rgba(52,211,153,0.04);
  transform: translateY(-2px);
}
.flutter-panel-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }

/* ─── Icon + Titles ─── */
.flutter-icon {
  display: flex; align-items: center; justify-content: center;
  width: 34px; height: 34px; background: rgba(168,85,247,0.1);
  border: 1px solid rgba(136,127,255,0.3); border-radius: 8px;
}
.flutter-panel-title {
  font-size: 12px; font-family: 'Outfit', sans-serif; font-weight: 700;
  letter-spacing: 1px; text-transform: uppercase; color: #fff;
}
.flutter-panel-sub {
  font-size: 9px; font-family: 'Outfit', sans-serif; font-weight: 500;
  letter-spacing: 0.5px; color: #887FFF;
}

/* ─── Pills ─── */
.flutter-pill {
  font-size: 9px; font-family: 'Outfit', sans-serif; font-weight: 600;
  border-radius: 4px; padding: 2px 7px;
}
.flutter-pill-purple { background: rgba(105,105,255,0.15); border: 1px solid rgba(136,127,255,0.3); color: #887FFF; }
.flutter-pill-green { background: rgba(16,185,129,0.1); border: 1px solid rgba(16,185,129,0.2); color: #34D399; }

/* ─── Frame time ─── */
.flutter-frame {
  font-size: 26px; font-family: 'Outfit', sans-serif; font-weight: 600;
  letter-spacing: -0.75px; color: #fff;
}
.flutter-panel-impeller:hover .flutter-frame { animation: flutter-pulse 1.5s ease-in-out infinite; }
@keyframes flutter-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
.flutter-unit { font-size: 15px; font-family: 'Outfit', sans-serif; font-weight: 700; color: #887FFF; }

/* ─── Chart ─── */
.flutter-chart { position: relative; height: 56px; background: #0E0E46; border: 1px solid rgba(168,85,247,0.2); border-radius: 8px; padding: 5px; }
.flutter-chart-svg { position: absolute; inset: 5px; }
.flutter-line { transition: stroke 0.3s, filter 0.3s; }
.flutter-panel-impeller:hover .flutter-line { stroke: #C084FC; filter: drop-shadow(0 0 4px rgba(192,132,252,0.6)); }
.flutter-chart-labels { position: absolute; bottom: 5px; left: 5px; right: 5px; display: flex; justify-content: space-between; pointer-events: none; }

/* ─── Stats ─── */
.flutter-stat { background: rgba(14,14,70,0.5); border-radius: 6px; padding: 8px 6px; border: 1px solid rgba(136,127,255,0.1); }
.flutter-stat-label { font-size: 8px; font-family: 'Outfit', sans-serif; color: #887FFF; }
.flutter-stat-val { font-size: 13px; font-family: 'Outfit', sans-serif; font-weight: 700; margin-top: 2px; }
.flutter-stat-sub { font-size: 8px; font-family: 'Outfit', sans-serif; color: #887FFF; margin-top: 1px; }

/* ─── Unification ─── */
.flutter-unify {
  font-size: 30px; font-family: 'Outfit', sans-serif; font-weight: 600;
  letter-spacing: -0.75px; color: #fff;
}

/* ─── Progress ─── */
.flutter-dot { width: 5px; height: 5px; border-radius: 50%; background: #EC4899; }
.flutter-label { font-size: 10px; font-family: 'Outfit', sans-serif; color: #CCD3FF; }
.flutter-percent { font-size: 9px; font-family: 'Outfit', sans-serif; font-weight: 500; }
.flutter-track { position: relative; height: 4px; border-radius: 9999px; background: rgba(30,41,59,0.5); overflow: hidden; transition: background 0.3s; }
.flutter-panel-code:hover .flutter-track { background: rgba(30,41,59,0.7); }
.flutter-fill { position: relative; height: 100%; border-radius: 9999px; transition: width 0.3s; }
.flutter-fill::after { content: ''; position: absolute; top: 0; left: -100%; width: 100%; height: 100%; background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent); }
.flutter-panel-code:hover .flutter-fill::after { animation: flutter-shimmer 1.5s ease-in-out infinite; }
@keyframes flutter-shimmer { 0% { left: -100%; } 100% { left: 100%; } }
</style>